// What kind of device and window the admin is running in — for the « Install
// the app » and « Turn on notifications » cards (admin PWA, 2026-10-01).
//
// Every function takes the navigator / window it reads, so it can be tested
// without touching the real globals; callers pass `navigator` / `window`.
import type { PushPlatform } from '@/lib/api';

export interface NavLike {
  userAgent?: string;
  maxTouchPoints?: number;
  /** iOS only: true inside a home-screen web app. */
  standalone?: boolean;
  serviceWorker?: unknown;
}

/** iPhone, iPod or iPad. iPadOS 13+ claims to be a Mac — a real Mac has no
 *  touch points, an iPad has several (same trick as lib/isIOS.ts). */
export function isIosDevice(nav: NavLike | undefined): boolean {
  const ua = nav?.userAgent ?? '';
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && (nav?.maxTouchPoints ?? 0) > 1;
}

/** The `platform` field of POST /push/subscribe. */
export function pushPlatform(nav: NavLike | undefined): PushPlatform {
  if (!nav) return 'other';
  if (isIosDevice(nav)) return 'ios';
  const ua = nav.userAgent ?? '';
  if (/Android/.test(ua)) return 'android';
  if (/Windows NT|Macintosh|X11|CrOS|Linux x86_64/.test(ua)) return 'desktop';
  return 'other';
}

/**
 * Running as the installed app (opened from the home-screen icon), not in a
 * browser tab. Android/desktop report it through the display-mode media query;
 * iPhone through `navigator.standalone`.
 */
export function isStandalone(win: {
  matchMedia?: (q: string) => { matches: boolean };
  navigator?: NavLike;
}): boolean {
  try {
    if (win.matchMedia?.('(display-mode: standalone)').matches) return true;
  } catch { /* an old browser without the query — fall through */ }
  return win.navigator?.standalone === true;
}

/** /admin and everything under /admin/ — never /administrator or /admin-sw.js. */
export function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

/**
 * Can this window subscribe to Web Push at all? An iPhone answers no in a
 * Safari TAB (push exists only inside the home-screen app, iOS 16.4+) — the
 * card tells that case apart before asking this.
 */
export function isPushSupported(win: {
  isSecureContext?: boolean;
  navigator?: NavLike;
  PushManager?: unknown;
  Notification?: unknown;
}): boolean {
  return win.isSecureContext === true
    && !!win.navigator?.serviceWorker
    && typeof win.PushManager !== 'undefined'
    && typeof win.Notification !== 'undefined';
}
